import { useState, lazy, Suspense } from 'react';
import type { SavedScenario } from '../../types/tax';
import { useScenarioContext } from '../../hooks/useScenariosContext';
import { formatCurrency } from '../../utils/formatting';
import EditScenarioModal from './EditScenarioModal';
import ExportIntentGate from '../export/ExportIntentGate';
import { trackExportAttempt } from '../../utils/analytics/intentTracking';

// Lazy load the export modal with heavy PDF libraries
const ExportModal = lazy(() => import('../export/ExportModal'));

const EXPORT_SOURCE = 'salary_comparison';

interface SalaryComparisonProps {
  className?: string;
}


export default function SalaryComparison({ className = '' }: SalaryComparisonProps) {
  const { scenarios, deleteScenario, clearAllScenarios, updateScenario } = useScenarioContext();
  const [showEditModal, setShowEditModal] = useState(false);
  const [scenarioToEdit, setScenarioToEdit] = useState<SavedScenario | null>(null);
  const [displayFrequency, setDisplayFrequency] = useState<'monthly' | 'yearly'>('monthly');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showIntentGate, setShowIntentGate] = useState(false);

  const handleDeleteScenario = (id: string) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      deleteScenario(id);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to delete all saved scenarios?')) {
      clearAllScenarios();
    }
  };


  const handleEditScenario = (scenario: SavedScenario) => {
    setScenarioToEdit(scenario);
    setShowEditModal(true);
  };

  const handleEditComplete = (id: string, updates: Partial<Pick<SavedScenario, 'name' | 'input' | 'result' | 'inputFrequency'>>) => {
    updateScenario(id, updates);
    setScenarioToEdit(null);
    setShowEditModal(false);
  };

  if (scenarios.length === 0) {
    return (
      <div className={`bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 ${className}`}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-slate-700 mb-4">
            <svg className="h-6 w-6 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0018 16.5h-2.25m-7.5-6h2.25m0 0h2.25m-2.25 0v2.25m0-2.25v-2.25" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">No Saved Scenarios</h3>
          <p className="text-gray-600 dark:text-slate-400 text-sm">
            Calculate a salary scenario first, then save it to start comparing different options.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden ${className}`}>
        <div className="bg-sa-green px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-white">Scenario Comparison</h3>
              <p className="text-green-100 text-sm">Compare up to 3 saved salary scenarios</p>
            </div>
            <div className="text-right">
              <span className="text-green-100 text-sm">
                {scenarios.length}/3 scenarios
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Display Frequency Selector and Action Buttons */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Display as:</span>
              <div className="flex space-x-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="monthly"
                    checked={displayFrequency === 'monthly'}
                    onChange={(e) => setDisplayFrequency(e.target.value as 'monthly' | 'yearly')}
                    className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Monthly</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="yearly"
                    checked={displayFrequency === 'yearly'}
                    onChange={(e) => setDisplayFrequency(e.target.value as 'monthly' | 'yearly')}
                    className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Yearly</span>
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  trackExportAttempt(EXPORT_SOURCE);
                  setShowIntentGate(true);
                }}
                className="btn-secondary text-sm px-3 py-1"
                disabled={scenarios.length === 0}
              >
                📤 Export & Share
              </button>
              <button
                onClick={handleClearAll}
                className="btn-secondary text-sm px-3 py-1"
                disabled={scenarios.length === 0}
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Scenarios Grid - Mobile: Stack, Desktop: Side by side */}
          <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-6">
            {scenarios.map((scenario) => {
              // Calculate amounts based on display frequency
              const desiredNetAmount = displayFrequency === 'monthly' 
                ? scenario.input.desiredNetIncome / 12
                : scenario.input.desiredNetIncome;
              
              const grossAmount = displayFrequency === 'monthly'
                ? scenario.result.grossIncome / 12  
                : scenario.result.grossIncome;
                
              const totalDeductionsAmount = displayFrequency === 'monthly'
                ? scenario.result.totalDeductions / 12
                : scenario.result.totalDeductions;
                
              const medicalAidAmount = displayFrequency === 'monthly'
                ? scenario.result.medicalAidCredit / 12
                : scenario.result.medicalAidCredit;
                
              const pensionAmount = displayFrequency === 'monthly'
                ? scenario.result.pensionContribution / 12
                : scenario.result.pensionContribution;

              return (
              <div key={scenario.id} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                {/* Scenario Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">
                      {scenario.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-slate-500">
                      Saved {scenario.createdAt.toLocaleDateString()}
                    </p>
                  </div>
                  
                  {/* Scenario Actions */}
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEditScenario(scenario)}
                      className="p-1 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
                      title="Edit scenario"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteScenario(scenario.id)}
                      className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Delete scenario"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Scenario Summary */}
                <div className="space-y-3">
                  {/* Desired Net Income */}
                  <div className="text-center bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">Desired Net Income</p>
                    <p className="text-lg font-bold text-blue-700 dark:text-blue-400">
                      {formatCurrency(desiredNetAmount)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-500">per {displayFrequency === 'monthly' ? 'month' : 'year'}</p>
                  </div>

                  {/* Required Gross Income */}
                  <div className="text-center bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">Required Gross Income</p>
                    <p className="text-lg font-bold text-sa-green">
                      {formatCurrency(grossAmount)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-500">per {displayFrequency === 'monthly' ? 'month' : 'year'}</p>
                  </div>

                  {/* Key Breakdown */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-400">Tax Rate:</span>
                      <span className="font-medium text-charcoal dark:text-slate-100">
                        {((scenario.result.totalDeductions / scenario.result.grossIncome) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-slate-400">Total Deductions:</span>
                      <span className="font-medium text-red-600 dark:text-red-400">
                        {formatCurrency(totalDeductionsAmount)}
                      </span>
                    </div>
                    {scenario.result.medicalAidCredit > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-slate-400">Medical Aid Credits:</span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          -{formatCurrency(medicalAidAmount)}
                        </span>
                      </div>
                    )}
                    {scenario.result.pensionContribution > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-slate-400">Pension Contribution:</span>
                        <span className="font-medium text-charcoal dark:text-slate-100">
                          {formatCurrency(pensionAmount)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {scenarios.length < 3 && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Tip:</strong> You can save up to 3 scenarios. Calculate another salary scenario to add it to the comparison.
              </p>
            </div>
          )}
        </div>
      </div>

      <EditScenarioModal
        isOpen={showEditModal}
        scenario={scenarioToEdit}
        onClose={() => {
          setShowEditModal(false);
          setScenarioToEdit(null);
        }}
        onSave={handleEditComplete}
      />
      
      {/* Intent gate — fires export_attempt on click and gate_seen on show */}
      <ExportIntentGate
        isOpen={showIntentGate}
        source={EXPORT_SOURCE}
        onClose={() => setShowIntentGate(false)}
        onProceed={() => {
          setShowIntentGate(false);
          setShowExportModal(true);
        }}
        calculationContext={
          scenarios.length > 0
            ? {
                desiredNetIncome: scenarios[0].input.desiredNetIncome,
                grossIncome: scenarios[0].result.grossIncome,
                netIncome: scenarios[0].result.netIncome,
                inputFrequency: displayFrequency,
              }
            : undefined
        }
      />

      {/* Export Modal for scenarios */}
      {showExportModal && scenarios.length > 0 && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-8 max-w-md">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        }>
          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            result={scenarios[0].result} // Use first scenario as placeholder for export features
            input={scenarios[0].input}   // Use first scenario as placeholder for export features
            inputFrequency={displayFrequency}
            scenarios={scenarios}
            title="Scenario Comparison Export"
          />
        </Suspense>
      )}
    </>
  );
}